import { FormEvent, useEffect, useMemo, useState } from "react";

export interface ServiceTypeFormValues {
  code: string;
  name: string;
  branchId: string | number;
  description?: string;
  isActive: boolean;
}

export interface StoreOption {
  value: string | number;
  label: string;
}

interface ServiceTypeCreateModalProps {
  storeOptions: StoreOption[];
  onClose: () => void;
  onSubmit: (values: ServiceTypeFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
  initialValues: ServiceTypeFormValues;
  mode?: "create" | "edit";
}

export const ServiceTypeCreateModal: React.FC<ServiceTypeCreateModalProps> = ({
  storeOptions,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialValues,
  mode = "create",
}) => {
  const resolvedInitialValues = useMemo<ServiceTypeFormValues>(() => {
    const fallbackBranchId =
      initialValues.branchId ?? storeOptions[0]?.value ?? "";

    return {
      code: initialValues.code ?? "",
      name: initialValues.name ?? "",
      branchId: fallbackBranchId,
      isActive: initialValues.isActive ?? true,
      description: initialValues.description ?? "",
    };
  }, [initialValues, storeOptions]);

  const [formValues, setFormValues] = useState<ServiceTypeFormValues>(resolvedInitialValues);

  useEffect(() => {
    setFormValues(resolvedInitialValues);
  }, [resolvedInitialValues]);

  const isEditMode = mode === "edit" || Boolean(initialValues?.code);

  const handleChange = <K extends keyof ServiceTypeFormValues>(
    key: K,
    value: ServiceTypeFormValues[K]
  ) => {
    setFormValues((previous) => ({ ...previous, [key]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const result = await onSubmit(formValues);
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-800">
              {isEditMode ? "Cập nhật dịch vụ" : "Thêm mới Loại dịch vụ"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isEditMode
                ? "Chỉnh sửa thông tin dịch vụ."
                : "Nhập thông tin dịch vụ mới cho hệ thống."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close create popup"
          >
            ×
          </button>
        </div>

        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label htmlFor="service-type-name" className="text-sm font-medium text-slate-700">
              Loại dịch vụ <span className="text-rose-500">*</span>
            </label>
            <input
              id="service-type-name"
              value={formValues.name}
              onChange={(event) => handleChange("name", event.target.value)}
              placeholder="Nhập tên loại dịch vụ"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm placeholder:text-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              required
              type="text"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="service-type-code" className="text-sm font-medium text-slate-700">
              Mã dịch vụ <span className="text-rose-500">*</span>
            </label>
            <input
              id="service-type-code"
              value={formValues.code}
              onChange={(event) => handleChange("code", event.target.value.toUpperCase())}
              placeholder="Nhập mã dịch vụ (ví dụ: GIAT_SAY)"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm placeholder:text-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 uppercase"
              required
              type="text"
              maxLength={50}
            />
          </div>

                
          {/* Tạm ẩn: danh mục đang dùng chung cho mọi chi nhánh — branchId lấy từ initialValues (0 = dùng chung).
              Mở lại khi cần tạo dịch vụ riêng theo chi nhánh.
          {!isEditMode && (
            <div className="space-y-2">
              <label htmlFor="service-type-store" className="text-sm font-medium text-slate-700">
                Đại lý áp dụng <span className="text-rose-500">*</span>
              </label>
              <select
                id="service-type-store"
                value={formValues.branchId}
                onChange={(event) => handleChange("branchId", event.target.value)}
                className="w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                required
              >
                {storeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )} */}


          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? isEditMode
                  ? "Đang cập nhật..."
                  : "Đang lưu..."
                : isEditMode
                  ? "Cập nhật"
                  : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceTypeCreateModal;
